/**
 * ErrorBoundary — catches render-time crashes anywhere in the tree and shows a
 * readable message instead of a blank white screen. Without this, any unhandled
 * exception during render unmounts the whole app and the user sees nothing.
 */
import React from 'react';

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Surface to the console for debugging.
    console.error('[ErrorBoundary]', error, info?.componentStack);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div style={{
        minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24, fontFamily: 'Inter, sans-serif',
      }}>
        <div style={{
          maxWidth: 520, width: '100%', background: 'var(--em-bg-card)', border: '1px solid #fee2e2',
          borderRadius: 14, padding: '28px 30px', boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
        }}>
          <div style={{ fontSize: 34, marginBottom: 12 }}>⚠️</div>
          <h2 style={{ margin: '0 0 8px', fontSize: 19, fontWeight: 700, color: '#111827' }}>
            Something went wrong on this page
          </h2>
          <p style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--em-fg5)', lineHeight: 1.6 }}>
            The page hit an unexpected error. You can try again, or head back to your dashboard.
          </p>
          <pre style={{
            background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca',
            borderRadius: 8, padding: '10px 12px', fontSize: 12, overflowX: 'auto',
            whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: '0 0 18px',
          }}>
            {String(this.state.error?.message || this.state.error)}
          </pre>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={this.reset} style={{
              background: '#f54b01', color: '#fff', border: 'none', borderRadius: 8,
              padding: '10px 18px', cursor: 'pointer', fontSize: 13, fontWeight: 600,
            }}>Try again</button>
            <button onClick={() => { window.location.href = '/'; }} style={{
              background: 'var(--em-bg-muted)', color: '#374151', border: 'none', borderRadius: 8,
              padding: '10px 18px', cursor: 'pointer', fontSize: 13, fontWeight: 600,
            }}>Go home</button>
          </div>
        </div>
      </div>
    );
  }
}
